/**
 * The wheels — and a track's volume and pan — as a MIDI file carries them.
 *
 * A bend lane and a mod wheel lane are curves; MIDI has only events. So a lane
 * leaves as a run of events sampled along it — at every point it has, and
 * between points often enough that no step is bigger than the wire can show —
 * and comes back through the import as the thinned ride it was.
 *
 * Pitch bend is a fraction of a *range* the file declares with a registered
 * parameter, two semitones unless it says otherwise. A lane that bends further
 * than two declares the range it needs, in whole semitones, so the far end of
 * the ride is not clipped to the default.
 */
import type { AutomationLane } from '@/domain/types';
import { automationRealValueAt } from '@/engine/automation';
import { instrumentParamDescriptor } from '@/engine/instruments/descriptors';
import { trackParamDescriptor } from '@/engine/effects/descriptors';
import type { MidiControllerEvent, MidiPitchBendEvent } from '@/util/midiFile';
import {
  CC_DATA_ENTRY_LSB,
  CC_DATA_ENTRY_MSB,
  CC_MOD_WHEEL,
  CC_PAN,
  CC_RPN_LSB,
  CC_RPN_MSB,
  CC_VOLUME,
  DEFAULT_BEND_RANGE_SEMITONES,
  levelController,
  panController,
} from './midiPerformance';

/** How often a moving lane is sampled between its own points, as a fraction of a beat. */
const SAMPLE_DIVISION = 16;

/**
 * Ticks at which to read a lane: every point of its own, and a grid between
 * them wherever the value is changing. A held stretch needs no grid.
 */
function sampleTicks(lane: AutomationLane, ppq: number): number[] {
  const points = [...lane.points].sort((a, b) => a.tick - b.tick);
  const step = Math.max(1, Math.round(ppq / SAMPLE_DIVISION));
  const ticks = new Set<number>();
  for (let i = 0; i < points.length; i++) {
    ticks.add(points[i].tick);
    const next = points[i + 1];
    if (!next || next.value === points[i].value) continue;
    for (let tick = points[i].tick + step; tick < next.tick; tick += step) ticks.add(tick);
  }
  return [...ticks].sort((a, b) => a - b);
}

/**
 * Drops the events inside a held stretch, keeping both of its ends. The end
 * matters as much as the start: the import draws a line between neighbouring
 * events, so a plateau that lost its last event would come back as a ramp
 * from its first event to whatever followed.
 */
function changesOnly<T extends { value: number }>(events: T[]): T[] {
  return events.filter((event, i) => {
    const previous = events[i - 1];
    const next = events[i + 1];
    return !previous || event.value !== previous.value || (next !== undefined && next.value !== event.value);
  });
}

/**
 * A bend lane as pitch bend events on a channel, and the range declaration
 * that makes them mean what the lane meant.
 */
export function bendLaneToEvents(
  lane: AutomationLane,
  ppq: number,
  channel: number,
): { bends: MidiPitchBendEvent[]; declaration: MidiControllerEvent[]; rangeSemitones: number } {
  const descriptor = instrumentParamDescriptor('synth', 'bend');
  if (!descriptor || lane.points.length === 0) return { bends: [], declaration: [], rangeSemitones: DEFAULT_BEND_RANGE_SEMITONES };

  const ticks = sampleTicks(lane, ppq);
  const semitones = ticks.map((tick) => ({ tick, st: automationRealValueAt(lane, descriptor, tick) ?? 0 }));
  const furthest = Math.max(...semitones.map((s) => Math.abs(s.st)));
  const rangeSemitones = Math.max(DEFAULT_BEND_RANGE_SEMITONES, Math.min(24, Math.ceil(furthest - 1e-9)));

  // Quantised to the wire's fourteen bits before the duplicates are dropped,
  // so a slow ramp does not leave a run of events that all read the same.
  const bends = changesOnly(
    semitones.map(({ tick, st }) => {
      const raw = Math.round(8192 + Math.max(-1, Math.min(1, st / rangeSemitones)) * 8192);
      const clamped = Math.max(0, Math.min(16383, raw));
      return { tick, channel, value: Math.max(-1, (clamped - 8192) / 8192) };
    }),
  );

  const first = Math.min(...ticks);
  const declaration: MidiControllerEvent[] =
    rangeSemitones === DEFAULT_BEND_RANGE_SEMITONES
      ? []
      : [
          { tick: first, channel, controller: CC_RPN_MSB, value: 0 },
          { tick: first, channel, controller: CC_RPN_LSB, value: 0 },
          { tick: first, channel, controller: CC_DATA_ENTRY_MSB, value: rangeSemitones },
          { tick: first, channel, controller: CC_DATA_ENTRY_LSB, value: 0 },
        ];
  return { bends, declaration, rangeSemitones };
}

/** A mod wheel lane as CC1 events on a channel. */
export function modWheelLaneToEvents(lane: AutomationLane, ppq: number, channel: number): MidiControllerEvent[] {
  const descriptor = instrumentParamDescriptor('synth', 'modWheel');
  if (!descriptor || lane.points.length === 0) return [];
  return changesOnly(
    sampleTicks(lane, ppq).map((tick) => ({
      tick,
      channel,
      controller: CC_MOD_WHEEL,
      value: Math.max(0, Math.min(127, Math.round((automationRealValueAt(lane, descriptor, tick) ?? 0) * 127))),
    })),
  );
}

/**
 * A track's volume lane as CC7, or its pan lane as CC10.
 *
 * The import reads a level as the *square* of its controller and combines
 * volume with expression, so the way back is the root, on the one controller
 * — expression stays unsent, which reads as full. A gain above unity has no
 * controller value and is held to 127; a track fader pushed past unity
 * comes back at unity. Pan is the pan formula backwards, with the same
 * lopsided centre the reader has, so a centre leaves as 64 and comes back
 * as exactly zero.
 */
export function trackLaneToEvents(lane: AutomationLane, ppq: number, channel: number): MidiControllerEvent[] {
  const paramId = lane.target.paramId;
  const descriptor = trackParamDescriptor(paramId);
  if (!descriptor || lane.points.length === 0) return [];
  if (paramId !== 'volume' && paramId !== 'pan') return [];
  const controller = paramId === 'volume' ? CC_VOLUME : CC_PAN;
  const toWire = paramId === 'volume' ? levelController : panController;
  return changesOnly(
    sampleTicks(lane, ppq).map((tick) => ({
      tick,
      channel,
      controller,
      value: toWire(automationRealValueAt(lane, descriptor, tick) ?? descriptor.default),
    })),
  );
}
